<script>
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import helpers from '../../../helpers.js'
import UserBadge from '@/components/parts/UserBadge.vue'
import {log} from "@/utils/clientLogger.js";
import OrderBadge from "@/components/resources/order-badges/components/OrderBadge.vue";

export default defineComponent({
  name: 'OrderInWork',
  components: {
    OrderBadge,
    UserBadge
  },
  props: {
    order: Object
  },
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      userData: {}
    };
  },
  mounted() {
    this.userData = this.$store.getters.userData;
  },
  methods: {
    orderToWork() {
      this.$swal.fire({
        title: `${this.userData?.username}, ${this.t('asks.confirmTakeOrder')}`,
        showCancelButton: true,
        confirmButtonText: this.t('buttons.confirm'),
        cancelButtonText: this.t('buttons.cancel')
      }).then((result) => {
        if (result.isConfirmed) {

          const payload = {};

          if (!this.order.draftsmanId) {
            payload.draftsmanId = this.userData._id;
          }

          this.$http.post(`/api/orders/to-work/${this.order._id}`, {
            draftsmanId: this.userData._id
          }).then(() => {
            this.$swal.fire(this.t('alerts.success'), '', 'success');
            this.$emit('order-taken', this.order._id);
          }).catch((error) => {
            log.error('View:OrderInWork:orderToWork', {'error': error.response?.data?.message});
            this.$swal.fire(this.t('alerts.error'), error.response?.data?.message, 'error');
          });
        }
      });
    },
    orderToArchive() {
      this.$swal.fire({
        title: this.t('alerts.warning'),
        text: `${this.userData?.username}, ${this.t('messages.confirmDeleteOrderPt1')} ${this.order.title} ${this.t('messages.confirmDeleteOrderPt2')}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.confirm'),
        cancelButtonText: this.t('buttons.cancel')
      }).then((result) => {
        if (result.isConfirmed) {
          this.$http.delete(`/api/orders/${this.order._id}`).then(() => {
            this.$swal.fire(this.t('alerts.success'), '', 'success');
          }).catch((e) => {
            this.$swal.fire(this.t('alerts.error'), e.response?.data?.message, 'error');
          });
        }
      });
    },
    escapeHtml(str) {
      return String(str ?? '').replace(/[&<>"']/g, s => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[s]));
    },
    noteToHtml(note) {
      const meta = [note.author, note.createdAt ? helpers.formatFullDate(note.createdAt) : '']
          .filter(Boolean).join(' • ');
      return '<div style="text-align:left;margin-bottom:8px;padding:8px 10px;background:#fff8e6;border-left:3px solid #ffc107;border-radius:6px;">'
          + `<div>${this.escapeHtml(note.text)}</div>`
          + (meta ? `<div style="font-size:11px;color:#adb5bd;margin-top:3px;">${this.escapeHtml(meta)}</div>` : '')
          + '</div>';
    },
    showClientNotes(notes = null, isAck = false) {
      const list = Array.isArray(notes) ? notes : (this.order?.clientId?.notes || []);
      if (!list.length) return;
      this.$swal.fire({
        title: this.t('labels.clientNote', 'Примітка клієнта'),
        html: list.map(n => this.noteToHtml(n)).join(''),
        icon: 'info',
        confirmButtonText: isAck ? this.t('buttons.acknowledged', 'Ознайомився') : this.t('buttons.confirm')
      });
    },
    timeSince(date) {
      return helpers.timeSince(date);
    },
    formatFullDate(date) {
      return helpers.formatFullDate(date);
    },
    idSlice(id) {
      return helpers.getLastCharacters(id);
    },
    noPatternsLabel(order) {
      if (order.noPatternsType === 'partial') {
        return this.t('labels.noPatternsPartialBadge', 'Є частково');
      }
      const base = order.noPatternsType === 'none_in_ours'
          ? this.t('labels.noPatternsNoneInOurs', 'Немає лекал в наших базах')
          : this.t('labels.noPatternsNoneInAny', 'Немає лекал в жодній з баз');
      return order.patternRequestSent
          ? `${base} · ${this.t('labels.patternRequestSent', 'Відправлено запит на отримання лекал')}`
          : base;
    },
    assemblyProgramLabels(values) {
      return helpers.getAssemblyProgramLabels(values);
    }
  },
  computed: {
    firstImage() {
      if (this.order?.imagesId && this.order.imagesId.length > 0) {
        return '/' + this.order.imagesId[0].path;
      }
      if (this.order?.carImageUrl) {
        return this.order.carImageUrl;
      }
      return '/uploads/websiteplanet-dummy-540X400.png';
    },
    getAlertClass() {
      return {
        cursor_pointer: true,
        'alert-warning': this.order.status === 'active' && helpers.getMinutesSince(this.order.updatedAt) > 5 && helpers.getMinutesSince(this.order.updatedAt) < 15,
        'alert-danger': this.order.status === 'active' && helpers.getMinutesSince(this.order.updatedAt) >= 15
      };
    },
    isOrderFromDistributor() {
      return this.order?.managerId?.role?.name === 'distributor';
    },
    currentUserRole() {
      return this.userData?.role?.name;
    },
    isAdminOrDraftsman() {
      return this.currentUserRole === 'admin' || this.currentUserRole === 'draftsman';
    }
  }
});
</script>

<template>
  <div :class="getAlertClass">
    <div class="card mb-3 mw-100 mx-auto">
      <span
          v-if="order.isUrgent"
          :class="isOrderFromDistributor ? 'bg-info' : 'bg-danger'"
          class="position-absolute top-0 start-0 translate-middle badge rounded-pill z-3"
      >
        {{ t('labels.urgent') }}
      </span>

      <img
          @click="$router.push(`/order/${order._id}`)"
          :src="firstImage"
          class="card-img-top"
          alt="">

      <button type="button"
              v-if="$store.getters.isManager"
              class="btn btn-close position-absolute end-0 p-3 z-2"
              @click="orderToArchive"
              :title="t('buttons.deleteOrder')">
      </button>

      <div @click="$router.push(`/order/${order._id}`)" class="card-body">
        <div class="d-flex flex-wrap gap-2 mb-2">
          <span v-if="order.status === 'cutting'" class="badge d-flex align-items-center gap-1 bg-success">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               class="icon icon-tabler icons-tabler-outline icon-tabler-cut me-1">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
            <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/>
            <path d="M9.15 14.85l8.85 -10.85"/>
            <path d="M9.15 9.15l8.85 10.85"/>
          </svg>
          {{ t('labels.cutting') }}
        </span>
          <span v-if="isOrderFromDistributor && isAdminOrDraftsman"
                class="badge d-flex align-items-center gap-1 bg-danger">
            {{ t('labels.orderFromDistributor') }}
          </span>
          <span v-if="order.hasProblematicPattern" class="badge d-flex align-items-center gap-1 bg-warning text-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 9v4" />
              <path d="M12 16v.01" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
            </svg>
            {{ t('labels.problemPattern', 'Проблемна викройка') }}
          </span>
          <span v-if="order.hasProblematicPackaging" class="badge d-flex align-items-center gap-1 bg-warning text-dark">
             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 9v4" />
              <path d="M12 16v.01" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
            </svg>
            {{ t('labels.problemPackaging', 'Проблема упаковки') }}
          </span>
          <span v-if="order.wasCut && (order.status === 'done' || order.status === 'closed')" class="badge d-flex align-items-center gap-1 bg-info text-dark">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M9.15 14.85l8.85 -10.85" />
              <path d="M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
              <path d="M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
              <path d="M9.15 9.15l8.85 10.85" />
            </svg>
            {{ t('log.fields.wasCutBadge') }}
          </span>
          <span v-if="order.assemblyPrograms && order.assemblyPrograms.length" class="badge bg-primary text-white mw-100 text-wrap text-break text-start d-inline-flex align-items-center"
                :title="t('labels.assemblyProgram', 'Програма збірки лекала')">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1" style="vertical-align:-0.15em;">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M4 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              <path d="M16 7V4a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              <path d="M4 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
              <path d="M16 17v-3a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" />
            </svg>{{ t('labels.assembledIn', 'Зібрано') }}: {{ assemblyProgramLabels(order.assemblyPrograms) }}
          </span>
          <span v-if="order.hasNoPatterns && order.noPatternsType === 'partial'" class="badge d-flex align-items-center gap-1 bg-warning text-dark np-badge mw-100"
                :title="t('labels.noPatternsPartialBadge', 'Є частково')">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M4 7h3a1 1 0 0 0 1 -1v-1a2 2 0 0 1 4 0v1a1 1 0 0 0 1 1h3a1 1 0 0 1 1 1v3a1 1 0 0 0 1 1h1a2 2 0 0 1 0 4h-1a1 1 0 0 0 -1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-1a2 2 0 0 0 -4 0v1a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h1a2 2 0 0 0 0 -4h-1a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1" />
            </svg>
            <span class="np-badge-text text-truncate">{{ t('labels.noPatternsPartialBadge', 'Є частково') }}</span>
          </span>
          <span v-else-if="order.hasNoPatterns" class="badge d-flex align-items-center gap-1 bg-secondary text-white np-badge mw-100"
                :style="order.patternRequestSent ? 'background-color:#f15a24 !important;' : ''"
                :title="noPatternsLabel(order)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="flex-shrink-0">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M3 3l18 18" />
              <path d="M4 7v-2a1 1 0 0 1 1 -1h2" />
              <path d="M4 17v2a1 1 0 0 0 1 1h2" />
              <path d="M16 4h3a1 1 0 0 1 1 1v3" />
              <path d="M20 16v3a1 1 0 0 1 -1 1h-3" />
            </svg>
            <span class="np-badge-text text-truncate">{{ noPatternsLabel(order) }}</span>
          </span>
        </div>
        <h6 class="card-title d-flex justify-content-between">
          <span>ID: <strong>{{ order?.humanId }}</strong></span>
          <OrderBadge v-if="order?.badgeId" :badge="order?.badgeId" :order-id="order._id"/>
        </h6>
        <h5 class="card-title">
          <span v-if="order.carBrand">
            <strong>{{ order.carBrand }}
              <span v-if="order.carModel">
                {{ order.carModel }} {{ order.carYear }}
              </span>
            </strong>
          </span>
          <span v-else>
            {{ order.title }}
          </span>
        </h5>
        <p class="card-text">{{ order.description }}</p>
      </div>

      <ul class="list-group list-group-flush">
        <li class="list-group-item">
          <strong>{{ t('tableheadings.client') }}: </strong>{{ order?.clientId?.name }}
          <span v-if="order?.clientId?.notes?.length"
                class="badge bg-warning text-dark ms-2 align-middle d-inline-flex align-items-center gap-1"
                style="cursor: pointer; vertical-align: middle;"
                :title="t('labels.clientNote', 'Примітка клієнта')"
                @click.stop="showClientNotes()">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M12 9h.01" />
              <path d="M11 12h1v4h1" />
              <path d="M12 3c7.2 0 9 1.8 9 9s-1.8 9 -9 9s-9 -1.8 -9 -9s1.8 -9 9 -9z" />
            </svg>
            {{ t('labels.clientNote', 'Примітка клієнта') }}
          </span>
        </li>
        <li class="list-group-item">
          <strong>
            {{ isOrderFromDistributor ? t('tableheadings.distributor') : t('tableheadings.manager') }}:
          </strong>
          <UserBadge :userId="order?.managerId?._id" :userName="order?.managerId?.username"/>
        </li>
        <li v-if="order?.draftsmanId" class="list-group-item">
          <small>
            <strong>{{ t('tableheadings.draftsman') }}: </strong>
            <UserBadge :userId="order?.draftsmanId?._id" :userName="order?.draftsmanId?.username"/>
          </small>
        </li>
        <li v-if="order.status === 'active' && $store.getters.isDraftsman" class="list-group-item">
          <button type="button" class="btn-warning btn mx-1 mx-md-2 w-100" @click="orderToWork">
            {{ t('buttons.takeToWork') }}
          </button>
        </li>
      </ul>

      <div class="card-body d-flex flex-column align-items-start justify-content-center">
        <span class="badge bg-secondary">
          <strong>{{ t('tableheadings.created') }}: </strong>
          {{ timeSince(order.createdAt) }}
          <span class="ms-1">({{ formatFullDate(order.createdAt) }})</span>
        </span>
        <span class="mt-2 badge bg-primary">
          <strong>{{ t('labels.lastUpdate') }}: </strong>
          {{ timeSince(order.updatedAt) }}
          <span class="ms-1">({{ formatFullDate(order.updatedAt) }})</span>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card-img-top {
  height: 20vh;
  object-fit: cover;
  background: var(--bs-gray-600);
}

.cursor_pointer {
  cursor: pointer;
}

.np-badge {
  max-width: 100%;
  min-width: 0;

  .np-badge-text {
    min-width: 0;
  }
}

.alert-warning {
  .card {
    animation: yellow_blink-animation 1s cubic-bezier(.68, -0.55, .27, 1.55) infinite;
  }
}

.alert-danger {
  .card {
    animation: red_blink-animation 1s cubic-bezier(.68, -0.55, .27, 1.55) infinite;
  }
}

@keyframes red_blink-animation {
  0% {
    box-shadow: 0 0 0 var(--bs-danger);
  }
  100% {
    box-shadow: 0 0 10px var(--bs-danger);
  }
}

@keyframes yellow_blink-animation {
  0% {
    box-shadow: 0 0 0 var(--bs-warning);
  }
  100% {
    box-shadow: 0 0 10px var(--bs-warning);
  }
}
</style>
